'use client';

import { useTranslations } from 'next-intl';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { NavMenuLink } from '@/features/layout/components/nav-menu-link';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { pagesRoutes } from '@/shared/constants/routes';
import { isGamesApp } from '@/shared/isGamesApp';
import { ChevronDown } from 'lucide-react';
import { CategoryMenuSection } from '@/features/layout/components/category-menu-section';

export const DesktopNavMenu = () => {
  const t = useTranslations('Home.header.desktopNavMenu');
  const { authenticatedUser } = useUser();

  const isWildMember = !!authenticatedUser?.isWildMember;
  const isClientApp = isGamesApp();

  const membershipMenuItem = isWildMember ? (
    <NavigationMenu.Item value='wild-unlimited'>
      <NavMenuLink
        href={pagesRoutes.wildUnlimited.href}
        isMainNav
        prefetch
        ariaLabel={`${t('navigateTo')} ${t('navMenuRoutes.wildUnlimited')}`}
        separator='right'
        data-gtm='cta'
        data-cta-name='nav_wild_unlimited'
      >
        {t('navMenuRoutes.wildUnlimited').toUpperCase()}
      </NavMenuLink>
    </NavigationMenu.Item>
  ) : (
    <NavigationMenu.Item value='join-wild'>
      <NavMenuLink
        href={pagesRoutes.joinWild.href}
        isMainNav
        prefetch
        ariaLabel={`${t('navigateTo')} ${t('navMenuRoutes.membership')}`}
        separator='right'
        data-gtm='cta'
        data-cta-name='nav_membership'
      >
        {t('navMenuRoutes.membership').toUpperCase()}
      </NavMenuLink>
    </NavigationMenu.Item>
  );

  return (
    <NavigationMenu.Root
      className='border-border-muted bg-background-contrast h-14 border-y font-bold'
      aria-label={t('mainNav')}
    >
      <NavigationMenu.List
        className='container mx-auto flex max-w-7xl items-center px-4 md:max-w-[1536px]'
        aria-label={t('primaryNavMenu')}
      >
        <NavigationMenu.Item value='find-games' className='relative'>
          <NavigationMenu.Trigger
            className='group font-body-bold hover:bg-background-subtle focus-visible:ring-primary data-[state=open]:bg-background-subtle mb-1 flex h-[54px] items-center px-4 transition-colors focus:outline-hidden focus-visible:ring-2'
            onClick={(e) => e.preventDefault()}
          >
            {t('navMenuRoutes.allGames').toUpperCase()}{' '}
            <ChevronDown className='mb-0 ml-2 h-4 w-4 transition-transform duration-200 ease-out group-data-[state=open]:rotate-180' />
          </NavigationMenu.Trigger>
          <NavigationMenu.Content
            className='animate-slide-down bg-background-panel data-[state=closed]:animate-slide-up absolute top-full left-0 z-50 -mt-0.5 w-[33.25rem] max-w-[90vw] origin-top'
            aria-label={`${t('navMenuRoutes.allGames')} ${t('submenu')}`}
          >
            <CategoryMenuSection />
          </NavigationMenu.Content>
        </NavigationMenu.Item>
        <NavigationMenu.Item value='free-to-play'>
          <NavMenuLink
            href={pagesRoutes.freeToPlay.href}
            isMainNav
            prefetch
            ariaLabel={`${t('navigateTo')} ${t('navMenuRoutes.freeToPlay')}`}
            separator='both'
            data-gtm='cta'
            data-cta-name='nav_free_to_play'
          >
            {t('navMenuRoutes.freeToPlay').toUpperCase()}
          </NavMenuLink>
        </NavigationMenu.Item>
        <NavigationMenu.Item value='new-releases'>
          <NavMenuLink
            href={pagesRoutes.newReleases.href}
            isMainNav
            prefetch
            ariaLabel={`${t('navigateTo')} ${t('navMenuRoutes.newReleases')}`}
            data-gtm='cta'
            data-cta-name='nav_new_releases'
          >
            {t('navMenuRoutes.newReleases').toUpperCase()}
          </NavMenuLink>
        </NavigationMenu.Item>
        <div className='ml-auto' />
        {isClientApp ? (
          <>
            <NavigationMenu.Item value='library'>
              <NavMenuLink
                href={pagesRoutes.library.href}
                isMainNav
                unlocalized
                prefetch={false}
                ariaLabel={`${t('navigateTo')} ${t('navMenuRoutes.library')}`}
                separator='right'
                data-gtm='cta'
                data-cta-name='nav_library'
              >
                {t('navMenuRoutes.library').toUpperCase()}
              </NavMenuLink>
            </NavigationMenu.Item>

            {membershipMenuItem}

            <NavigationMenu.Item value='support'>
              <NavMenuLink
                href={pagesRoutes.support.href}
                isMainNav
                unlocalized
                ariaLabel={`${t('navigateTo')} ${t('navMenuRoutes.support')}`}
                data-gtm='cta'
                data-cta-name='nav_support'
              >
                {t('navMenuRoutes.support').toUpperCase()}
              </NavMenuLink>
            </NavigationMenu.Item>
          </>
        ) : (
          <>
            {membershipMenuItem}

            <NavigationMenu.Item value='more' className='relative'>
              <NavigationMenu.Trigger
                className='group font-body-bold hover:bg-background-subtle focus-visible:ring-primary data-[state=open]:bg-background-subtle mb-1 flex h-[54px] items-center px-4 transition-colors focus:outline-hidden focus-visible:ring-2'
                onClick={(e) => e.preventDefault()}
              >
                {t('navMenuRoutes.more').toUpperCase()}{' '}
                <ChevronDown className='mb-0 ml-2 h-4 w-4 transition-transform duration-200 ease-out group-data-[state=open]:rotate-180' />
              </NavigationMenu.Trigger>
              <NavigationMenu.Content
                className='animate-slide-down bg-background-panel data-[state=closed]:animate-slide-up absolute top-full right-0 z-50 -mt-0.5 w-[10rem] max-w-[90vw] origin-top'
                aria-label={`${t('navMenuRoutes.more')} ${t('submenu')}`}
              >
                <div className='p-2' role='region' aria-labelledby='more-heading'>
                  <h2 id='more-heading' className='sr-only'>
                    {t('navMenuRoutes.more')} {t('navigation')}
                  </h2>
                  <NavMenuLink
                    href={pagesRoutes.support.href}
                    isMainNav
                    unlocalized
                    ariaLabel={`${t('navigateTo')} ${t('navMenuRoutes.support')}`}
                    className='h-11 p-2'
                    data-gtm='cta'
                    data-cta-name='nav_more_support'
                  >
                    {t('navMenuRoutes.support')}
                  </NavMenuLink>
                  <NavMenuLink
                    href={pagesRoutes.gamesApp.href}
                    isMainNav
                    prefetch
                    ariaLabel={`${t('navigateTo')} ${t('navMenuRoutes.gamesApp')}`}
                    className='h-11 p-2'
                    data-gtm='cta'
                    data-cta-name='nav_more_games_app'
                  >
                    {t('navMenuRoutes.gamesApp')}
                  </NavMenuLink>
                </div>
              </NavigationMenu.Content>
            </NavigationMenu.Item>
          </>
        )}
      </NavigationMenu.List>
    </NavigationMenu.Root>
  );
};
